import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import BASE_URL from "../../config/config";
import Captcha from "./Captcha";
import "../../css/loginForm.css";

const OTPLogin = ({ captchaVerified, setMessage }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [expiresAt, setExpiresAt] = useState(null);
  const [internalCaptchaVerified, setInternalCaptchaVerified] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  const [message, setLocalMessage] = useState(''); // state جدید برای پیام‌ها
  const [messageType, setMessageType] = useState(''); // نوع پیام (error/success)

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setCsrfToken(data.csrfToken);
        } else {
          console.error('Failed to fetch CSRF token:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };

    fetchCsrfToken();
  }, []);
  
  const authAxios = axios.create({
    baseURL: BASE_URL,
    withCredentials: true
  });
  
  // نمایش پیام‌ها
  const showMessage = (msg, type = "error") => {
    // استفاده از setMessage اگر به عنوان prop ارسال شده
    if (setMessage && typeof setMessage === 'function') {
      setMessage(msg);
    }
    
    // استفاده از toast اگر موجود است
    if (toast && typeof toast === 'object') {
      type === "success" ? toast.success(msg) : toast.error(msg);
    }
    
    // نمایش پیام در کامپوننت
    setLocalMessage(msg);
    setMessageType(type);
    
    // پاک کردن خودکار پیام بعد از 5 ثانیه
    setTimeout(() => {
      setLocalMessage('');
      setMessageType('');
    }, 5000);
  };
  
  // درخواست OTP
  const requestOTP = async (e) => {
    if (e) e.preventDefault();
    
    // پاک کردن پیام قبلی
    setLocalMessage('');
    setMessageType('');
    
    if (!mobile || mobile.length < 10) {
      showMessage("لطفا شماره موبایل معتبر وارد کنید");
      return;
    }
    
    if (!internalCaptchaVerified) {
      showMessage("لطفاً کد امنیتی را تأیید کنید");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (csrfToken) {
        headers['CSRF-Token'] = csrfToken;
      }
      
      const response = await authAxios.post('/admin/request-otp', 
        { mobile },
        { headers }
      );
      
      if (response.data.success) {
        showMessage("کد تایید با موفقیت ارسال شد", "success");
        setStep(2);
        
        if (response.data.expiresAt) {
          setExpiresAt(new Date(response.data.expiresAt));
          const now = new Date();
          const diffSeconds = Math.floor((new Date(response.data.expiresAt) - now) / 1000);
          setTimer(diffSeconds > 0 ? diffSeconds : 0);
        } else {
          setTimer(120);
        }
        
        startCountdown();
      }
    } catch (error) {
      // console.error("Error requesting OTP:", error);
      
      let errorMessage = "خطا در ارسال کد تایید";
      
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
        
        if (error.response.status === 400 && error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = "خطای شبکه، لطفا اتصال اینترنت خود را بررسی کنید";
      }
      
      showMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // تایمر شمارش معکوس
  const startCountdown = () => {
    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  };
  
  // تایید OTP و ورود
  const verifyOTP = async (e, otpValue = null) => {
    if (e) e.preventDefault();
    
    // پاک کردن پیام قبلی
    setLocalMessage('');
    setMessageType('');
    
    const codeToVerify = otpValue || otp;
    
    if (!codeToVerify || codeToVerify.length < 6) {
      showMessage("لطفا کد تایید را وارد کنید");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (csrfToken) {
        headers['CSRF-Token'] = csrfToken;
      }
      
      const response = await authAxios.post('/admin/verify-otp', 
        { mobile, code: codeToVerify },
        { headers }
      );
      
      if (response.data.success) {
        showMessage("ورود با موفقیت انجام شد", "success");
        
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          authAxios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          
          if (response.data.userInfo) {
            login(response.data.userInfo);
            const redirectUrl = response.data.userInfo?.role === "admin" ? "/admin" : "/dashboard";
            navigate(redirectUrl);
          }
        }
      }
    } catch (error) {
      // console.error("Error verifying OTP:", error);
      
      let errorMessage = "کد تایید نامعتبر است";
      
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
        
        if (error.response.status === 400 && error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = "خطای شبکه، لطفا اتصال اینترنت خود را بررسی کنید";
      }
      
      showMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    
    if (value.length === 6 && !loading) {
      verifyOTP(null, value);
    }
  };

  useEffect(() => {
    setInternalCaptchaVerified(false);
  }, [step]);

  return (
    <div>
      <form onSubmit={step === 1 ? requestOTP : (e) => verifyOTP(e)} className="otp-login-form">
        {step === 1 ? (
          <>
            <div className="input-group">
              <label className="mb-2 text-lg" htmlFor="mobile">شماره موبایل</label>
              <input
                type="tel"
                id="mobile"
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 11))
                }
                className="text-center form-input"
                placeholder="09123456789"
                dir="ltr"
                required
              />
            </div>

            <button 
              type="submit" 
              disabled={loading || !internalCaptchaVerified}
              className="extra-button mt-6"
            >
              {loading ? "در حال ارسال..." : "دریافت کد تایید"}
            </button>
          </>
        ) : (
          <>
            <div className="input-group">
              <label htmlFor="otp">کد تایید ارسال شده به {mobile}</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={handleOtpChange}
                className="text-center"
                placeholder="------"
                maxLength="6"
                dir="ltr"
                required
              />

              {timer > 0 && (
                <div className="otp-timer">
                  <span>زمان باقیمانده: {formatTime(timer)}</span>
                </div>
              )}
            </div>

            <button type="submit" disabled={loading}>
              {loading ? "در حال بررسی..." : "تایید و ورود"}
            </button>

            <div className="otp-actions">
              <button
                type="button"
                className="text-button"
                onClick={() => setStep(1)}
              >
                تغییر شماره موبایل
              </button>

              <button
                type="button"
                className={`text-button ${timer > 0 ? "disabled" : ""}`}
                onClick={requestOTP}
                disabled={timer > 0 || loading}
              >
                ارسال مجدد کد
              </button>
            </div>
          </>
        )}
      </form>

      {/* نمایش پیام در زیر فرم */}
      {message && (
        <div className={`message ${messageType}`} style={{
          marginTop: '15px',
          padding: '10px',
          borderRadius: '5px',
          textAlign: 'center',
          backgroundColor: messageType === 'success' ? '#d4edda' : '#f8d7da',
          color: messageType === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${messageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      <Captcha 
        onVerify={(isVerified) => setInternalCaptchaVerified(isVerified)}
        onExpire={() => setInternalCaptchaVerified(false)}
        timeout={120000}
        regenerateOnError={true}
      />
    </div>
  );
};

export default OTPLogin;